import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import TouchDragGhost from '../TouchDragGhost';
import {
  getAugmentByValue,
  CLASH_AUGMENT_OPTIONS,
  CLASH_TACTICAL_POINTS_MAX_PER_CLASH,
  getAugmentDisplayName,
  getAugmentDisplaySvg,
  getAugmentBaseCode,
  getOwnedStyleIdsForPoolAugment,
  getPoolAugmentDisplaySvg,
  getClashAugmentOption,
  getClashAugmentsForHelp,
  getClashAugmentPoolHelpLead,
  isGameOnRoundCard
} from '../../utils/augments';
import { AugmentPoolStyleDot } from '../AugmentPoolStyleDot';
import AugmentHelpPopover from '../AugmentHelpPopover';
import AugmentHelpItems from '../AugmentHelpItems';
import {
  getPoolAugmentStylePreference,
  setPoolAugmentStylePreference,
  restorePoolStylePreferenceFromPlacedRef
} from '../../utils/augmentStylePreference';
import {
  claimTouchDragHandle,
  elementUnderTouch,
  shouldIgnoreNativeDragDuringTouch
} from '../../utils/touchDragActivation';
import { getClashAugmentCodeOnGame, findPlacementForClashAugment } from './clashBettingUtils';
import ClubCrest from '../ClubCrest';
import TeamName from '../TeamName';
import { formatMatchupLabel } from '../../utils/formatTeamDisplayName';
import { getGameStatusLabel } from '../../utils/gameStatusLabel';
import './ClashUI.css';

const CLASH_AUGMENT_CARD_COLOR = {
  sa: 'gold',
  bs: 'cyan',
  eif: 'green',
  gd: 'gold'
};

function sameGameId(a, b) {
  return String(a) === String(b);
}

function isGameLiveStatus(g) {
  const s = g?.status;
  return s === 'live' || s === 'in_play' || s === 'active';
}

/**
 * Bottom sheet (mobile) / modal (desktop): pick clash play for one opponent on one match.
 * Pool ↔ match cards; touch arms drag immediately (no long-press / scroll on the plays).
 */
export function ClashAugmentSheet({
  open,
  onClose,
  competitionLabel,
  clashLeagueName,
  game,
  augmentMapKey,
  opponentUsername,
  bets,
  gameId,
  disabled,
  tacticalSpentOnThisKey,
  leagueCode,
  roundGames = [],
  inventory,
  onPick,
  onStyleChange
}) {
  const gid = String(gameId);
  const [draggingClashAugment, setDraggingClashAugment] = useState(null);
  const [draggingFromClashGameId, setDraggingFromClashGameId] = useState(null);
  const [dragOverClashGameId, setDragOverClashGameId] = useState(null);
  const [isClashTouchDragging, setIsClashTouchDragging] = useState(false);
  const [clashTouchCurrentPos, setClashTouchCurrentPos] = useState(null);
  const [clashAugmentSpreadWave, setClashAugmentSpreadWave] = useState(null);
  const clashSpreadTimeoutRef = useRef(null);
  /** `{ value, source, sourceGameId? }` while a touch drag is armed. */
  const clashTouchDragRef = useRef(null);
  /** Chip under the finger; cloned at its layout size for the drag preview. */
  const clashTouchDragSourceElRef = useRef(null);
  const [poolStyleTick, setPoolStyleTick] = useState(0);

  const getClashPoolStyleId = useCallback(
    (poolValue) => {
      void poolStyleTick;
      const pref = getPoolAugmentStylePreference(poolValue);
      return pref === undefined ? null : pref;
    },
    [poolStyleTick]
  );

  const selectClashPoolStyle = useCallback(
    (poolValue, styleId) => {
      if (onStyleChange) {
        onStyleChange(poolValue, styleId);
      } else {
        setPoolAugmentStylePreference(poolValue, styleId);
      }
      setPoolStyleTick((n) => n + 1);
    },
    [onStyleChange]
  );

  const buildClashPoolDragPayload = useCallback(
    (poolValue, source, extra = {}) => {
      const styleId = getClashPoolStyleId(poolValue);
      return { value: poolValue, source, styleId: styleId ?? null, ...extra };
    },
    [getClashPoolStyleId]
  );

  const pct = Math.min(100, (tacticalSpentOnThisKey / CLASH_TACTICAL_POINTS_MAX_PER_CLASH) * 100);
  const over = tacticalSpentOnThisKey > CLASH_TACTICAL_POINTS_MAX_PER_CLASH;

  const orderedGames = useMemo(() => {
    const rg = Array.isArray(roundGames) ? roundGames : [];
    if (rg.length === 0) return game ? [game] : [];
    const primaryId = String(gameId);
    const primary = rg.find((g) => String(g.gameId) === primaryId) ?? game;
    if (!primary) return [...rg];
    const othersInOrder = rg.filter((g) => String(g.gameId) !== String(primary.gameId));
    return [primary, ...othersInOrder];
  }, [roundGames, gameId, game]);

  const clashAugmentUsedByGame = useMemo(() => {
    const map = {};
    if (!bets || !augmentMapKey) return map;
    for (const g of orderedGames) {
      const code = getClashAugmentCodeOnGame(bets, String(g.gameId), augmentMapKey);
      if (code) {
        map[code] = String(g.gameId);
        map[getAugmentBaseCode(code)] = String(g.gameId);
      }
    }
    return map;
  }, [bets, augmentMapKey, orderedGames]);

  const triggerClashAugmentSpreadWave = useCallback((gameIds, color) => {
    const c = color || 'gold';
    const targets = new Set(gameIds.map((id) => String(id)));
    if (clashSpreadTimeoutRef.current) {
      clearTimeout(clashSpreadTimeoutRef.current);
      clashSpreadTimeoutRef.current = null;
    }
    setClashAugmentSpreadWave({ targets, color: c });
    clashSpreadTimeoutRef.current = setTimeout(() => {
      setClashAugmentSpreadWave(null);
      clashSpreadTimeoutRef.current = null;
    }, 1200);
  }, []);

  useEffect(
    () => () => {
      if (clashSpreadTimeoutRef.current) clearTimeout(clashSpreadTimeoutRef.current);
    },
    []
  );

  const applyPick = useCallback(
    (value, targetGameId, dragData) => {
      if (disabled) return;
      const tid = targetGameId != null ? String(targetGameId) : gid;
      const base = getAugmentBaseCode(String(value));
      onPick?.(base, tid, dragData);
      const col = CLASH_AUGMENT_CARD_COLOR[base] || 'gold';
      triggerClashAugmentSpreadWave([tid], col);
    },
    [disabled, gid, onPick, triggerClashAugmentSpreadWave]
  );

  const applyRemove = useCallback(
    (sourceGameId) => {
      if (disabled) return;
      const sid = sourceGameId != null ? String(sourceGameId) : gid;
      const placedRef = getClashAugmentCodeOnGame(bets, sid, augmentMapKey);
      if (placedRef) {
        restorePoolStylePreferenceFromPlacedRef(placedRef);
        setPoolStyleTick((n) => n + 1);
      }
      onPick?.('', sid);
    },
    [augmentMapKey, bets, disabled, gid, onPick]
  );

  const handleClashDrop = useCallback(
    (targetGameId, dragData) => {
      if (!dragData || disabled) return;
      const { value, source, sourceGameId } = dragData;
      if (source === 'game' && sameGameId(sourceGameId, targetGameId)) return;
      applyPick(value, targetGameId, dragData);
    },
    [applyPick, disabled]
  );

  const handleClashRemove = useCallback(
    (sourceGameId) => {
      applyRemove(sourceGameId);
    },
    [applyRemove]
  );

  const resetClashDragState = useCallback(() => {
    clashTouchDragSourceElRef.current = null;
    setDraggingClashAugment(null);
    setDraggingFromClashGameId(null);
    setDragOverClashGameId(null);
    clashTouchDragRef.current = null;
    setIsClashTouchDragging(false);
    setClashTouchCurrentPos(null);
  }, []);

  const armClashTouchDrag = useCallback(
    ({ payload, sourceEl, startPos, draggingAugment, draggingFromClashGameId: fromGid }) => {
      clashTouchDragRef.current = payload;
      clashTouchDragSourceElRef.current = sourceEl ?? null;
      setClashTouchCurrentPos(startPos);
      setDraggingClashAugment(draggingAugment);
      setDraggingFromClashGameId(fromGid);
      setIsClashTouchDragging(true);
    },
    []
  );

  const handleKeydown = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
      }
    },
    [open, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [handleKeydown]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!isClashTouchDragging) return;
    const preventTouchScroll = (e) => {
      e.preventDefault();
    };
    window.addEventListener('touchmove', preventTouchScroll, { passive: false });
    return () => {
      window.removeEventListener('touchmove', preventTouchScroll);
    };
  }, [isClashTouchDragging]);

  useEffect(() => {
    if (!isClashTouchDragging) return;
    const handleTouchCancel = () => resetClashDragState();
    window.addEventListener('touchend', handleTouchCancel, { passive: true });
    window.addEventListener('touchcancel', handleTouchCancel, { passive: true });
    return () => {
      window.removeEventListener('touchend', handleTouchCancel);
      window.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [isClashTouchDragging, resetClashDragState]);

  useEffect(() => {
    if (!open) {
      resetClashDragState();
    }
  }, [open, resetClashDragState]);

  if (!open || !game || !augmentMapKey) return null;

  const handleClear = () => {
    if (disabled) return;
    const placement = findPlacementForClashAugment(bets, augmentMapKey);
    if (placement?.gameId) applyRemove(placement.gameId);
  };

  const hasAnyClashPlacement = Boolean(findPlacementForClashAugment(bets, augmentMapKey));

  return (
    <div
      className="clash-sheet-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="clash-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="clash-sheet-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="clash-sheet-header">
          <div className="clash-sheet-header-main">
            <h2 id="clash-sheet-title" className="clash-sheet-title">
              Clash pick
            </h2>
            <p className="clash-sheet-sub">
              vs <strong>{opponentUsername}</strong>
            </p>
            {(clashLeagueName || competitionLabel) && (
              <p className="clash-sheet-league-line">
                {clashLeagueName ? <strong className="clash-sheet-league-name">{clashLeagueName}</strong> : null}
                {clashLeagueName && competitionLabel ? (
                  <span className="clash-sheet-league-sep" aria-hidden>
                    {' '}
                    ·{' '}
                  </span>
                ) : null}
                {competitionLabel ? <span className="clash-sheet-competition">{competitionLabel}</span> : null}
              </p>
            )}
          </div>
          <button type="button" className="clash-sheet-close" aria-label="Close" onClick={() => onClose?.()}>
            ×
          </button>
        </div>
        <div
          className="tactical-points-meter clash-sheet-header-meter"
          aria-label={`Clash tactical points for this opponent: ${tacticalSpentOnThisKey} of ${CLASH_TACTICAL_POINTS_MAX_PER_CLASH}`}
        >
          <div className="tactical-points-meter-row">
            <span className="tactical-points-meter-label">Tactical points (this opponent)</span>
            <span
              className={`tactical-points-meter-count ${over ? 'tactical-points-meter-count--over' : ''}`}
            >
              {tacticalSpentOnThisKey}/{CLASH_TACTICAL_POINTS_MAX_PER_CLASH}
            </span>
          </div>
          <div className="tactical-points-meter-track">
            <div
              className={`tactical-points-meter-fill ${tacticalSpentOnThisKey >= CLASH_TACTICAL_POINTS_MAX_PER_CLASH ? 'tactical-points-meter-fill--full' : ''}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <div className="clash-sheet-body clash-sheet-body--with-fixed-pool">
          <div className="clash-sheet-clash-pool-sticky">
            <div className="augments-label-row clash-sheet-augment-label-row augment-subsection-help-row">
              <h3 className="augments-label">Clash plays</h3>
              <AugmentHelpPopover
                title="Clash plays"
                triggerAriaLabel="What do clash plays do?"
                dialogAriaLabel="Clash play descriptions"
              >
                <p className="augment-help-popover__lead">{getClashAugmentPoolHelpLead()}</p>
                <AugmentHelpItems augments={getClashAugmentsForHelp()} />
              </AugmentHelpPopover>
            </div>
            <div className="augment-pool clash-sheet-clash-augment-pool-wrap">
            <div
              className={`augment-cards clash-sheet-clash-augment-cards ${draggingFromClashGameId ? 'augment-cards--glow' : ''}`}
              onDragOver={(e) => {
                if (disabled) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = draggingFromClashGameId ? 'move' : 'none';
              }}
              onDrop={(e) => {
                if (disabled) return;
                e.preventDefault();
                try {
                  const data = JSON.parse(e.dataTransfer.getData('application/json'));
                  if (data.source === 'game' && data.sourceGameId != null) {
                    handleClashRemove(data.sourceGameId);
                  }
                } catch (_) {}
                setDraggingClashAugment(null);
                setDraggingFromClashGameId(null);
                clashTouchDragRef.current = null;
              }}
            >
              {CLASH_AUGMENT_OPTIONS.map((o) => {
                const poolStyleId = getClashPoolStyleId(o.value);
                const poolDisplaySvg =
                  getPoolAugmentDisplaySvg(o.value, poolStyleId, inventory) ?? o.svg;
                const hasPoolStyles = getOwnedStyleIdsForPoolAugment(o.value, inventory ?? {}).length > 0;
                const color = CLASH_AUGMENT_CARD_COLOR[o.value] || 'gold';
                const usedOnGameId = clashAugmentUsedByGame[o.value];
                const isUsed = Boolean(usedOnGameId);
                const isDragging = draggingClashAugment === o.value;
                const augmentDisabled = isUsed || disabled;
                const elsewhereGame =
                  isUsed && Array.isArray(roundGames) && roundGames.length > 0
                    ? roundGames.find((g) => String(g.gameId) === String(usedOnGameId)) ?? null
                    : null;
                const isActiveHere = usedOnGameId === gid;

                return (
                  <div
                    key={o.value}
                    draggable={!augmentDisabled}
                    ref={augmentDisabled ? undefined : claimTouchDragHandle}
                    className={`augment-card augment-card--${color} clash-sheet-clash-augment-card ${isUsed ? 'augment-card--used' : ''} ${isDragging ? 'augment-card--dragging' : ''} ${isActiveHere && isUsed ? 'clash-sheet-clash-augment-card--here' : ''}`}
                    onDragStart={(e) => {
                      if (augmentDisabled) return;
                      if (shouldIgnoreNativeDragDuringTouch(null, clashTouchDragRef.current)) {
                        e.preventDefault();
                        return;
                      }
                      setDraggingClashAugment(o.value);
                      setDraggingFromClashGameId(null);
                      e.dataTransfer.setData(
                        'application/json',
                        JSON.stringify(buildClashPoolDragPayload(o.value, 'pool'))
                      );
                      e.dataTransfer.effectAllowed = 'copyMove';
                    }}
                    onDragEnd={() => {
                      setDraggingClashAugment(null);
                      setDraggingFromClashGameId(null);
                    }}
                    onTouchStart={(e) => {
                      if (augmentDisabled) return;
                      if (e.target?.closest?.('.augment-style-dot, .augment-style-menu')) return;
                      const touch = e.touches[0];
                      armClashTouchDrag({
                        startPos: { x: touch.clientX, y: touch.clientY },
                        payload: buildClashPoolDragPayload(o.value, 'pool'),
                        sourceEl: e.currentTarget,
                        draggingAugment: o.value,
                        draggingFromClashGameId: null
                      });
                    }}
                    onTouchMove={(e) => {
                      if (!clashTouchDragRef.current || isUsed) return;
                      const touch = e.touches[0];
                      const pos = { x: touch.clientX, y: touch.clientY };
                      e.preventDefault();
                      setClashTouchCurrentPos(pos);
                      const element = elementUnderTouch(touch.clientX, touch.clientY);
                      const gameCard = element?.closest('.clash-sheet-game-card[data-game-id]');
                      const augmentArea = element?.closest('.clash-sheet-clash-pool-sticky') || element?.closest('.clash-sheet-clash-augment-pool-wrap');
                      const td = clashTouchDragRef.current;
                      if (gameCard) {
                        setDragOverClashGameId(gameCard.dataset.gameId);
                      } else if (augmentArea && td?.source === 'game') {
                        setDragOverClashGameId('augment-area');
                      } else {
                        setDragOverClashGameId(null);
                      }
                    }}
                    onTouchEnd={(e) => {
                      const td = clashTouchDragRef.current;
                      if (!td || isUsed) return;
                      const touch = e.changedTouches[0];
                      const element = elementUnderTouch(touch.clientX, touch.clientY);
                      const gameCard = element?.closest('.clash-sheet-game-card[data-game-id]');
                      const augmentArea =
                        element?.closest('.clash-sheet-clash-pool-sticky') ||
                        element?.closest('.clash-sheet-clash-augment-pool-wrap');
                      if (gameCard) {
                        const targetId = gameCard.dataset.gameId;
                        if (targetId) handleClashDrop(targetId, td);
                      } else if (augmentArea && td.source === 'game' && td.sourceGameId) {
                        handleClashRemove(td.sourceGameId);
                      }
                      resetClashDragState();
                    }}
                    title={
                      isUsed && elsewhereGame
                        ? `${o.shortDesc || ''} On ${formatMatchupLabel(elsewhereGame.homeTeam, elsewhereGame.awayTeam)}. Drag from that match to move or here to remove.`
                        : isUsed
                          ? `${o.shortDesc || ''} Drag from match to move or to pool to remove.`
                          : `${o.shortDesc || ''}. Drag to a match below.`
                    }
                  >
                    <span className="augment-card-tactical-cost" aria-label={`${o.tacticalPointsCost} tactical points`}>
                      {o.tacticalPointsCost}
                    </span>
                    <span className="augment-card-desc">{o.displayName}</span>
                    {poolDisplaySvg ? (
                      <img src={poolDisplaySvg} alt="" className="augment-card-img" draggable={false} />
                    ) : null}
                    {isUsed && elsewhereGame ? (
                      <span className="clash-sheet-clash-augment-elsewhere-hint">
                        {formatMatchupLabel(elsewhereGame.homeTeam, elsewhereGame.awayTeam)}
                      </span>
                    ) : null}
                    {hasPoolStyles ? (
                      <AugmentPoolStyleDot
                        poolValue={o.value}
                        inventory={inventory}
                        selectedStyleId={poolStyleId}
                        disabled={disabled}
                        onSelect={(styleId) => selectClashPoolStyle(o.value, styleId)}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
            </div>
          </div>

          <div
            className={`clash-sheet-scroll${isClashTouchDragging || draggingClashAugment ? ' clash-sheet-scroll--touch-dragging' : ''}`}
          >
          <div className="clash-sheet-matches-label">Matches</div>
          <p className="clash-sheet-matches-help">
            Top slot shows the <strong>match play</strong> already set on that match. Bottom slot is your
            <strong> clash play</strong> for this opponent.
          </p>
          <div className="clash-sheet-matches" role="list">
            {orderedGames.map((g) => {
              const rowGid = String(g.gameId);
              const isPrimary = rowGid === gid;
              const bet = bets?.[rowGid] ?? bets?.[g.gameId];
              const matchAug = bet?.augment;
              const matchAugMeta = matchAug ? getAugmentByValue(matchAug) : null;
              const clashCode = getClashAugmentCodeOnGame(bets, rowGid, augmentMapKey);
              const clashBase = clashCode ? getAugmentBaseCode(clashCode) : '';
              const clashColor = clashBase ? CLASH_AUGMENT_CARD_COLOR[clashBase] || 'gold' : null;
              const clashDef = clashCode ? getClashAugmentOption(clashCode) : null;
              const clashDisplaySvg =
                clashCode && clashDef
                  ? getAugmentDisplaySvg(clashCode, inventory) ?? clashDef.svg
                  : null;
              const gameFinished = !isGameOnRoundCard(g);
              const gameLive = isGameLiveStatus(g);
              const isDroppable = !disabled && isGameOnRoundCard(g) && !gameLive;
              const gameLocked = disabled && !gameFinished && !gameLive;

              return (
                <div
                  key={rowGid}
                  role="listitem"
                  data-game-id={rowGid}
                  className={`game-card clash-sheet-game-card ${isPrimary ? 'clash-sheet-game-card--primary' : ''} ${gameLocked ? 'game-card--locked' : ''} ${gameLive ? 'game-card--live' : ''} ${gameFinished ? 'game-card--finished' : ''} ${isDroppable ? 'game-card--droppable' : ''} ${dragOverClashGameId != null && sameGameId(dragOverClashGameId, rowGid) ? 'game-card--drag-over' : ''} ${clashAugmentSpreadWave?.targets.has(rowGid) ? `game-card--augment-spread game-card--augment-${clashAugmentSpreadWave.color}` : ''}`}
                  onDragOver={
                    isDroppable
                      ? (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect = 'move';
                          setDragOverClashGameId(String(rowGid));
                        }
                      : undefined
                  }
                  onDragLeave={
                    isDroppable
                      ? (e) => {
                          if (!e.currentTarget.contains(e.relatedTarget)) setDragOverClashGameId(null);
                        }
                      : undefined
                  }
                  onDrop={
                    isDroppable
                      ? (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOverClashGameId(null);
                          try {
                            const data = JSON.parse(e.dataTransfer.getData('application/json'));
                            handleClashDrop(g.gameId, data);
                          } catch (_) {}
                          setDraggingClashAugment(null);
                          setDraggingFromClashGameId(null);
                        }
                      : undefined
                  }
                >
                  <div className="game-card-header clash-sheet-game-card-header">
                    <span className="game-date">
                      {g.gameDate
                        ? new Date(g.gameDate).toLocaleString(undefined, {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : '—'}
                    </span>
                    <div className="game-card-header-right">
                      <div
                        className="clash-sheet-game-augment-indicator"
                        title={matchAug ? getAugmentDisplayName(matchAug) : 'No match play'}
                      >
                        <span className="clash-sheet-game-augment-indicator-label">Match play</span>
                        {matchAug && matchAugMeta ? (
                          <img
                            src={matchAugMeta.svg}
                            alt={getAugmentDisplayName(matchAug)}
                            className="clash-sheet-game-augment-indicator-icon"
                          />
                        ) : (
                          <span className="clash-sheet-game-augment-indicator-empty" aria-hidden>
                            —
                          </span>
                        )}
                      </div>
                      {gameFinished ? (
                        <span className="game-status game-status--finished">
                          {getGameStatusLabel(g, roundGames)}
                        </span>
                      ) : null}
                      {gameLive ? <span className="game-status game-status--live">LIVE</span> : null}
                      {!gameFinished && !gameLive && !isDroppable ? (
                        <span className="game-status game-status--upcoming">Scheduled</span>
                      ) : null}
                    </div>
                  </div>

                  <div className="game-teams clash-sheet-game-teams">
                    <div className="game-team game-team--home">
                      <div className="team-head">
                        {leagueCode ? (
                          <ClubCrest leagueCode={leagueCode} teamLabel={g.homeTeam} teamTla={g.homeTeamTla} className="team-crest" />
                        ) : null}
                        <TeamName name={g.homeTeam} />
                      </div>
                    </div>
                    <div className="game-vs-augment game-vs-augment--compact clash-sheet-vs-column">
                      <span className="game-vs">vs</span>
                      <div className="game-augment-slot clash-sheet-augment-slot clash-sheet-clash-match-slot">
                        {!isDroppable || gameFinished || gameLive ? (
                          clashCode && clashDef ? (
                            <div
                              className={`augment-slot-btn augment-slot-btn--filled augment-slot-btn--static augment-slot-btn--${clashColor}`}
                              title={clashDef.displayName}
                            >
                              <img
                                src={clashDisplaySvg ?? clashDef.svg}
                                alt={clashDef.displayName}
                                className="augment-badge augment-badge-img clash-sheet-clash-slot-img"
                              />
                            </div>
                          ) : (
                            <span className="augment-empty clash-sheet-augment-empty">—</span>
                          )
                        ) : clashCode && clashDef ? (
                          <div
                            draggable
                            ref={claimTouchDragHandle}
                            className={`augment-slot-btn augment-slot-btn--filled augment-slot-btn--draggable augment-slot-btn--${clashColor}`}
                            onDragStart={(e) => {
                              e.stopPropagation();
                              if (shouldIgnoreNativeDragDuringTouch(null, clashTouchDragRef.current)) {
                                e.preventDefault();
                                return;
                              }
                              setDraggingClashAugment(clashCode);
                              setDraggingFromClashGameId(g.gameId);
                              e.dataTransfer.setData(
                                'application/json',
                                JSON.stringify({ value: clashCode, source: 'game', sourceGameId: g.gameId })
                              );
                              e.dataTransfer.effectAllowed = 'move';
                            }}
                            onDragEnd={() => {
                              setDraggingClashAugment(null);
                              setDraggingFromClashGameId(null);
                            }}
                            onTouchStart={(e) => {
                              e.stopPropagation();
                              const touch = e.touches[0];
                              armClashTouchDrag({
                                startPos: { x: touch.clientX, y: touch.clientY },
                                payload: { value: clashCode, source: 'game', sourceGameId: g.gameId },
                                sourceEl: e.currentTarget,
                                draggingAugment: clashCode,
                                draggingFromClashGameId: g.gameId
                              });
                            }}
                            onTouchMove={(e) => {
                              if (!clashTouchDragRef.current) return;
                              const touch = e.touches[0];
                              const pos = { x: touch.clientX, y: touch.clientY };
                              e.preventDefault();
                              e.stopPropagation();
                              setClashTouchCurrentPos(pos);
                              const element = elementUnderTouch(touch.clientX, touch.clientY);
                              const gameCardEl = element?.closest('.clash-sheet-game-card[data-game-id]');
                              const augmentArea =
                                element?.closest('.clash-sheet-clash-pool-sticky') ||
                                element?.closest('.clash-sheet-clash-augment-pool-wrap');
                              if (gameCardEl) {
                                setDragOverClashGameId(gameCardEl.dataset.gameId);
                              } else if (augmentArea) {
                                setDragOverClashGameId('augment-area');
                              } else {
                                setDragOverClashGameId(null);
                              }
                            }}
                            onTouchEnd={(e) => {
                              const td = clashTouchDragRef.current;
                              if (!td) return;
                              e.stopPropagation();
                              const touch = e.changedTouches[0];
                              const element = elementUnderTouch(touch.clientX, touch.clientY);
                              const gameCardEl = element?.closest('.clash-sheet-game-card[data-game-id]');
                              const augmentArea =
                                element?.closest('.clash-sheet-clash-pool-sticky') ||
                                element?.closest('.clash-sheet-clash-augment-pool-wrap');
                              if (gameCardEl) {
                                const targetGameId = gameCardEl.dataset.gameId;
                                if (targetGameId && !sameGameId(targetGameId, g.gameId)) {
                                  handleClashDrop(targetGameId, td);
                                }
                              } else if (augmentArea) {
                                handleClashRemove(g.gameId);
                              }
                              resetClashDragState();
                            }}
                            title={`${clashDef.displayName} — drag to another match or to clash plays above to remove`}
                          >
                            <img
                              src={clashDisplaySvg ?? clashDef.svg}
                              alt={clashDef.displayName}
                              className="augment-badge augment-badge-img clash-sheet-clash-slot-img"
                            />
                          </div>
                        ) : (
                          <span className="augment-empty clash-sheet-augment-empty">—</span>
                        )}
                      </div>
                    </div>
                    <div className="game-team game-team--away">
                      <div className="team-head">
                        {leagueCode ? (
                          <ClubCrest leagueCode={leagueCode} teamLabel={g.awayTeam} teamTla={g.awayTeamTla} className="team-crest" />
                        ) : null}
                        <TeamName name={g.awayTeam} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="clash-sheet-actions">
            <button
              type="button"
              className="sc-btn sc-btn-secondary"
              disabled={disabled || !hasAnyClashPlacement}
              onClick={handleClear}
            >
              Clear
            </button>
            <button type="button" className="sc-btn sc-btn-primary" onClick={() => onClose?.()}>
              Done
            </button>
          </div>
          </div>
        </div>
      </div>

      {isClashTouchDragging && clashTouchCurrentPos && draggingClashAugment ? (
        <TouchDragGhost
          x={clashTouchCurrentPos.x}
          y={clashTouchCurrentPos.y}
          sourceEl={clashTouchDragSourceElRef.current}
          zIndex={10001}
        />
      ) : null}
    </div>
  );
}
